/**
 * 利用統計ページ
 *
 * 請求根拠として取引先別の月次利用回数を表示する。
 * 月選択、サマリーカード、取引先別集計テーブル、CSVエクスポート機能を提供。
 */

import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Title,
  Group,
  Paper,
  Text,
  Table,
  Button,
  Loader,
  Alert,
  SimpleGrid,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { Download, Users, FileText, Calendar } from "lucide-react";
import { fetchUsageStats } from "../services/usageStatsService";
import type { UsageStatsResponse, CustomerUsageStats } from "@ibs-ocr/shared";
import type { LogStatus } from "@ibs-ocr/shared";

/** ステータス別のカラム定義 */
const STATUS_COLUMNS: { key: LogStatus; label: string }[] = [
  { key: "received", label: "受信" },
  { key: "processing", label: "処理中" },
  { key: "completed", label: "完了" },
  { key: "error", label: "エラー" },
  { key: "rejected", label: "却下" },
  { key: "pending", label: "保留" },
  { key: "warning", label: "警告" },
  { key: "aborted", label: "中断" },
];

/**
 * CSVエクスポート処理
 */
function exportCsv(data: UsageStatsResponse) {
  const bom = "\uFEFF";
  const headers = [
    "顧客ID",
    "顧客名",
    ...STATUS_COLUMNS.map((c) => c.label),
    "初回",
    "再処理",
    "合計",
  ];

  const rows = data.customers.map((c) =>
    [
      c.customerId,
      c.customerName,
      ...STATUS_COLUMNS.map((col) => String(c.counts[col.key] ?? 0)),
      String(c.firstTime),
      String(c.reprocess),
      String(c.total),
    ].join(",")
  );

  // 合計行
  const totals = [
    "",
    "合計",
    ...STATUS_COLUMNS.map((col) =>
      String(
        data.customers.reduce((sum, c) => sum + (c.counts[col.key] ?? 0), 0)
      )
    ),
    String(data.customers.reduce((sum, c) => sum + c.firstTime, 0)),
    String(data.customers.reduce((sum, c) => sum + c.reprocess, 0)),
    String(data.summary.totalCount),
  ].join(",");

  const csv = bom + [headers.join(","), ...rows, totals].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `利用統計_${data.year}年${data.month}月.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export function UsageStatsPage() {
  const now = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(
    new Date(now.getFullYear(), now.getMonth())
  );
  const [usageStats, setUsageStats] = useState<UsageStatsResponse | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1; // 0-indexed → 1-indexed
      const data = await fetchUsageStats(year, month);
      setUsageStats(data);
    } catch (err) {
      console.error("利用統計取得エラー:", err);
      setError("統計情報の取得に失敗しました");
      setUsageStats(null);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** 合計行を算出 */
  const totalRow: CustomerUsageStats | null = usageStats
    ? {
        customerId: "",
        customerName: "合計",
        counts: STATUS_COLUMNS.reduce(
          (acc, col) => {
            acc[col.key] = usageStats.customers.reduce(
              (sum, c) => sum + (c.counts[col.key] ?? 0),
              0
            );
            return acc;
          },
          {} as Record<LogStatus, number>
        ),
        firstTime: usageStats.customers.reduce(
          (sum, c) => sum + c.firstTime,
          0
        ),
        reprocess: usageStats.customers.reduce(
          (sum, c) => sum + c.reprocess,
          0
        ),
        total: usageStats.summary.totalCount,
      }
    : null;

  return (
    <Container size="xl" py="xl">
      {/* ヘッダー */}
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <Calendar size={28} />
          <Title order={1}>利用統計</Title>
        </Group>
        <MonthPickerInput
          label="対象月"
          value={selectedDate}
          onChange={(date) => {
            if (date) setSelectedDate(date);
          }}
          maxDate={new Date()}
          style={{ width: 200 }}
        />
      </Group>

      {/* エラー表示 */}
      {error && (
        <Alert color="red" mb="lg">
          {error}
        </Alert>
      )}

      {/* ローディング */}
      {loading && (
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      )}

      {/* データ表示 */}
      {!loading && usageStats && (
        <>
          {/* サマリーカード */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} mb="lg">
            <Paper shadow="xs" p="lg" radius="md" withBorder>
              <Group gap="sm">
                <Users size={20} />
                <Text size="sm" c="dimmed">
                  対象顧客数
                </Text>
              </Group>
              <Text size="xl" fw={700} mt="xs">
                {usageStats.summary.totalCustomers}
              </Text>
            </Paper>
            <Paper shadow="xs" p="lg" radius="md" withBorder>
              <Group gap="sm">
                <FileText size={20} />
                <Text size="sm" c="dimmed">
                  合計処理件数
                </Text>
              </Group>
              <Text size="xl" fw={700} mt="xs">
                {usageStats.summary.totalCount}
              </Text>
            </Paper>
          </SimpleGrid>

          {/* 取引先別集計テーブル */}
          <Paper shadow="xs" p="lg" radius="md" withBorder mb="lg">
            <Group justify="space-between" mb="md">
              <Title order={3}>取引先別集計</Title>
              <Button
                variant="light"
                leftSection={<Download size={16} />}
                onClick={() => exportCsv(usageStats)}
              >
                CSVエクスポート
              </Button>
            </Group>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>顧客名</Table.Th>
                  {STATUS_COLUMNS.map((col) => (
                    <Table.Th key={col.key} style={{ textAlign: "right" }}>
                      {col.label}
                    </Table.Th>
                  ))}
                  <Table.Th style={{ textAlign: "right" }}>初回</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>再処理</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>合計</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {usageStats.customers.map((customer) => (
                  <Table.Tr key={customer.customerId}>
                    <Table.Td>{customer.customerName}</Table.Td>
                    {STATUS_COLUMNS.map((col) => (
                      <Table.Td
                        key={col.key}
                        style={{ textAlign: "right" }}
                      >
                        {customer.counts[col.key] ?? 0}
                      </Table.Td>
                    ))}
                    <Table.Td style={{ textAlign: "right" }}>
                      {customer.firstTime}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      {customer.reprocess}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      {customer.total}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {/* 合計行 */}
                {totalRow && (
                  <Table.Tr style={{ fontWeight: 700 }}>
                    <Table.Td>{totalRow.customerName}</Table.Td>
                    {STATUS_COLUMNS.map((col) => (
                      <Table.Td
                        key={col.key}
                        style={{ textAlign: "right" }}
                      >
                        {totalRow.counts[col.key] ?? 0}
                      </Table.Td>
                    ))}
                    <Table.Td style={{ textAlign: "right" }}>
                      {totalRow.firstTime}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      {totalRow.reprocess}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      {totalRow.total}
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </>
      )}
    </Container>
  );
}
